import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowUp, ArrowDown, Trash2, Plus, Save, X, Rows, Columns, Send } from 'lucide-react';
import { TOKENS as T, SERIF_FONT as SERIF } from '@/components/PublicationDesignRenderer';
import PublishModal from '@/components/PublishModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const clone = (o) => JSON.parse(JSON.stringify(o));

// Auto-growing textarea that matches the publication typography
const AutoTextarea = ({ value, onChange, style, testid, ...props }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) {
      ref.current.style.height = 'auto';
      ref.current.style.height = `${ref.current.scrollHeight}px`;
    }
  }, [value]);
  return (
    <textarea
      ref={ref}
      data-testid={testid}
      value={value || ''}
      onChange={onChange}
      rows={1}
      style={{
        width: '100%',
        resize: 'none',
        overflow: 'hidden',
        border: `1px dashed ${T.RULE_LINE}`,
        borderRadius: '4px',
        background: T.WHITE,
        padding: '0.35rem 0.5rem',
        ...style,
      }}
      {...props}
    />
  );
};

const TextInput = ({ value, onChange, style, testid, ...props }) => (
  <input
    type="text"
    data-testid={testid}
    value={value || ''}
    onChange={onChange}
    style={{
      width: '100%',
      border: `1px dashed ${T.RULE_LINE}`,
      borderRadius: '4px',
      background: T.WHITE,
      padding: '0.35rem 0.5rem',
      ...style,
    }}
    {...props}
  />
);

const CtrlButton = ({ onClick, title, children, testid, danger }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    data-testid={testid}
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '1.7rem',
      height: '1.7rem',
      borderRadius: '4px',
      border: `1px solid ${danger ? '#dc2626' : T.RULE_LINE}`,
      background: danger ? '#fef2f2' : T.WHITE,
      color: danger ? '#dc2626' : T.SLATE,
      cursor: 'pointer',
    }}
  >
    {children}
  </button>
);

const BlockControls = ({ onUp, onDown, onDelete, testidPrefix }) => (
  <div style={{ display: 'flex', gap: '0.3rem', flexShrink: 0 }}>
    <CtrlButton onClick={onUp} title="Move up" testid={`${testidPrefix}-up`}><ArrowUp size={14} /></CtrlButton>
    <CtrlButton onClick={onDown} title="Move down" testid={`${testidPrefix}-down`}><ArrowDown size={14} /></CtrlButton>
    <CtrlButton onClick={onDelete} title="Delete" testid={`${testidPrefix}-delete`} danger><Trash2 size={14} /></CtrlButton>
  </div>
);

const AddBlockBar = ({ onAdd, testidPrefix }) => {
  const types = [
    ['body', 'Paragraph'],
    ['h2', 'Subheading'],
    ['h3', 'Label'],
    ['callout', 'Callout'],
    ['table', 'Table'],
  ];
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', margin: '0.5rem 0 1rem 0', alignItems: 'center' }}>
      <span style={{ color: T.MIST, fontSize: '0.7rem', fontFamily: SERIF, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Add</span>
      {types.map(([type, label]) => (
        <button
          key={type}
          type="button"
          onClick={() => onAdd(type)}
          data-testid={`${testidPrefix}-${type}`}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
            fontFamily: SERIF, fontSize: '0.78rem', color: T.DEEP_TEAL,
            border: `1px solid ${T.RULE_LINE}`, borderRadius: '999px',
            padding: '0.2rem 0.6rem', background: T.WHITE, cursor: 'pointer',
          }}
        >
          <Plus size={12} /> {label}
        </button>
      ))}
    </div>
  );
};

const blockRow = { display: 'flex', gap: '0.6rem', alignItems: 'flex-start', margin: '0.4rem 0' };

export default function PublicationEditor({ publication, onSaved, onCancel, onPublished }) {
  const [data, setData] = useState(() => ({
    title_line1: publication.title_line1 || publication.title || '',
    title_line2: publication.title_line2 || '',
    subtitle: publication.subtitle || '',
    author: publication.author || '',
    pub_date: publication.pub_date || '',
    keywords: clone(publication.keywords || []),
    sections: clone(publication.sections || []),
  }));
  const [saving, setSaving] = useState(false);
  const [newKeyword, setNewKeyword] = useState('');
  const [showPublish, setShowPublish] = useState(false);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    axios.get(`${API}/test-categories`)
      .then((res) => setCategories((res.data || []).filter((c) => c.enabled)))
      .catch(() => setCategories([]));
  }, []);

  const mutate = useCallback((fn) => {
    setData((prev) => {
      const next = clone(prev);
      fn(next);
      return next;
    });
  }, []);

  // ---- field helpers ----
  const setField = (field, value) => mutate((d) => { d[field] = value; });

  // ---- keyword helpers ----
  const addKeyword = () => {
    const kw = newKeyword.trim();
    if (!kw) return;
    mutate((d) => { d.keywords.push(kw); });
    setNewKeyword('');
  };
  const removeKeyword = (i) => mutate((d) => { d.keywords.splice(i, 1); });

  // ---- section helpers ----
  const setSectionTitle = (si, value) => mutate((d) => { d.sections[si].title = value; });
  const moveSection = (si, dir) => mutate((d) => {
    const ni = si + dir;
    if (ni < 0 || ni >= d.sections.length) return;
    [d.sections[si], d.sections[ni]] = [d.sections[ni], d.sections[si]];
  });
  const deleteSection = (si) => {
    if (!window.confirm('Delete this entire section?')) return;
    mutate((d) => { d.sections.splice(si, 1); });
  };
  const addSection = () => mutate((d) => {
    d.sections.push({ number: String(d.sections.length + 1).padStart(2, '0'), title: 'NEW SECTION', blocks: [{ type: 'body', text: '' }] });
  });

  // ---- block helpers ----
  const setBlockText = (si, bi, value) => mutate((d) => { d.sections[si].blocks[bi].text = value; });
  const moveBlock = (si, bi, dir) => mutate((d) => {
    const blocks = d.sections[si].blocks;
    const ni = bi + dir;
    if (ni < 0 || ni >= blocks.length) return;
    [blocks[bi], blocks[ni]] = [blocks[ni], blocks[bi]];
  });
  const deleteBlock = (si, bi) => mutate((d) => { d.sections[si].blocks.splice(bi, 1); });
  const addBlock = (si, type) => mutate((d) => {
    const nb = type === 'table'
      ? { type: 'table', has_header: true, rows: [['Column 1', 'Column 2'], ['', '']] }
      : { type, text: '' };
    d.sections[si].blocks.push(nb);
  });

  // ---- table helpers ----
  const setCell = (si, bi, ri, ci, value) => mutate((d) => { d.sections[si].blocks[bi].rows[ri][ci] = value; });
  const addRow = (si, bi) => mutate((d) => {
    const rows = d.sections[si].blocks[bi].rows;
    const cols = rows[0] ? rows[0].length : 2;
    rows.push(Array(cols).fill(''));
  });
  const deleteRow = (si, bi, ri) => mutate((d) => { d.sections[si].blocks[bi].rows.splice(ri, 1); });
  const addCol = (si, bi) => mutate((d) => { d.sections[si].blocks[bi].rows.forEach((r) => r.push('')); });
  const deleteCol = (si, bi, ci) => mutate((d) => { d.sections[si].blocks[bi].rows.forEach((r) => r.splice(ci, 1)); });
  const toggleHeader = (si, bi) => mutate((d) => { const b = d.sections[si].blocks[bi]; b.has_header = !b.has_header; });

  const persist = async () => {
    await axios.put(`${API}/admin/publications/${publication.id}/structured`, {
      title_line1: data.title_line1,
      title_line2: data.title_line2,
      subtitle: data.subtitle,
      author: data.author,
      pub_date: data.pub_date,
      keywords: data.keywords,
      sections: data.sections,
    }, { withCredentials: true });
  };

  const save = async () => {
    setSaving(true);
    try {
      await persist();
      toast.success('Changes saved');
      onSaved();
    } catch (e) {
      console.error('Save failed:', e);
      toast.error(e.response?.data?.detail || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const openPublish = async () => {
    setSaving(true);
    try {
      await persist();
      setShowPublish(true);
    } catch (e) {
      console.error('Save before publish failed:', e);
      toast.error(e.response?.data?.detail || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handlePublishSubmit = async (publishData) => {
    try {
      await axios.post(`${API}/admin/publications/${publication.id}/publish`, publishData, { withCredentials: true });
      toast.success('Published to the Publication Center!');
      setShowPublish(false);
      if (onPublished) onPublished();
      else onSaved();
    } catch (e) {
      console.error('Publish failed:', e);
      toast.error(e.response?.data?.detail || 'Failed to publish');
    }
  };

  // ---- block renderers (edit mode) ----
  const renderBlockEditor = (section, si, block, bi) => {
    const ctrl = (
      <BlockControls
        onUp={() => moveBlock(si, bi, -1)}
        onDown={() => moveBlock(si, bi, 1)}
        onDelete={() => deleteBlock(si, bi)}
        testidPrefix={`block-${si}-${bi}`}
      />
    );

    if (block.type === 'table') {
      const rows = block.rows || [];
      return (
        <div key={bi} style={blockRow}>
          <div style={{ flex: 1, overflowX: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.4rem' }}>
              <label style={{ fontFamily: SERIF, fontSize: '0.78rem', color: T.SLATE, display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                <input type="checkbox" checked={!!block.has_header} onChange={() => toggleHeader(si, bi)} data-testid={`table-${si}-${bi}-header`} />
                First row is header
              </label>
              <CtrlButton onClick={() => addRow(si, bi)} title="Add row" testid={`table-${si}-${bi}-addrow`}><Rows size={14} /></CtrlButton>
              <CtrlButton onClick={() => addCol(si, bi)} title="Add column" testid={`table-${si}-${bi}-addcol`}><Columns size={14} /></CtrlButton>
            </div>
            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
              <tbody>
                {rows.map((row, ri) => (
                  <tr key={ri}>
                    {row.map((cellVal, ci) => (
                      <td key={ci} style={{ border: `1px solid ${T.RULE_LINE}`, padding: '2px' }}>
                        <AutoTextarea
                          value={cellVal}
                          onChange={(e) => setCell(si, bi, ri, ci, e.target.value)}
                          testid={`cell-${si}-${bi}-${ri}-${ci}`}
                          style={{ border: 'none', fontFamily: SERIF, fontSize: '0.85rem', color: T.OBSIDIAN }}
                        />
                      </td>
                    ))}
                    <td style={{ padding: '0 0 0 4px', verticalAlign: 'middle' }}>
                      <CtrlButton onClick={() => deleteRow(si, bi, ri)} title="Delete row" testid={`table-${si}-${bi}-delrow-${ri}`} danger><Trash2 size={12} /></CtrlButton>
                    </td>
                  </tr>
                ))}
                <tr>
                  {(rows[0] || []).map((_, ci) => (
                    <td key={ci} style={{ textAlign: 'center', padding: '2px' }}>
                      <CtrlButton onClick={() => deleteCol(si, bi, ci)} title="Delete column" testid={`table-${si}-${bi}-delcol-${ci}`} danger><X size={12} /></CtrlButton>
                    </td>
                  ))}
                  <td />
                </tr>
              </tbody>
            </table>
          </div>
          {ctrl}
        </div>
      );
    }

    const styleByType = {
      h2: { fontFamily: SERIF, fontSize: '1.2rem', fontWeight: 700, color: T.DEEP_TEAL },
      h3: { fontFamily: SERIF, fontSize: '1rem', fontWeight: 700, color: T.GOLD, fontVariant: 'small-caps' },
      callout: { fontFamily: SERIF, fontStyle: 'italic', color: T.CALLOUT_TEXT, background: T.GOLD_BG, borderLeft: `6px solid ${T.GOLD}` },
      body: { fontFamily: SERIF, fontSize: '1rem', color: T.OBSIDIAN, lineHeight: 1.6 },
    };
    const label = { body: 'Paragraph', h2: 'Subheading (H2)', h3: 'Label (H3)', callout: 'Callout' }[block.type] || block.type;

    return (
      <div key={bi} style={blockRow}>
        <div style={{ flex: 1 }}>
          <div style={{ color: T.MIST, fontSize: '0.65rem', fontFamily: SERIF, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.15rem' }}>{label}</div>
          <AutoTextarea
            value={block.text}
            onChange={(e) => setBlockText(si, bi, e.target.value)}
            testid={`blocktext-${si}-${bi}`}
            style={styleByType[block.type] || styleByType.body}
          />
        </div>
        {ctrl}
      </div>
    );
  };

  return (
    <div data-testid="publication-editor" style={{ backgroundColor: T.WHITE, padding: '1.5rem clamp(1rem, 4vw, 2.5rem)', borderRadius: '6px', boxShadow: '0 10px 40px rgba(0,0,0,0.08)' }}>
      {/* Toolbar */}
      <div style={{ position: 'sticky', top: '5.5rem', zIndex: 30, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', background: T.WHITE, padding: '0.5rem 0', marginBottom: '1rem', borderBottom: `2px solid ${T.GOLD}` }}>
        <span style={{ fontFamily: SERIF, fontWeight: 700, color: T.DEEP_TEAL }}>Editing publication</span>
        <div style={{ display: 'flex', gap: '0.6rem' }}>
          <button
            type="button"
            onClick={onCancel}
            data-testid="editor-cancel-button"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', padding: '0.5rem 1rem', borderRadius: '6px', border: `1px solid ${T.RULE_LINE}`, background: T.WHITE, color: T.SLATE, fontWeight: 700, cursor: 'pointer' }}
          >
            <X size={16} /> Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            data-testid="editor-save-button"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', padding: '0.5rem 1.2rem', borderRadius: '6px', border: 'none', background: T.DEEP_TEAL, color: T.WHITE, fontWeight: 700, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}
          >
            <Save size={16} /> {saving ? 'Saving...' : 'Save Changes'}
          </button>
          <button
            type="button"
            onClick={openPublish}
            disabled={saving}
            data-testid="editor-save-publish-button"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', padding: '0.5rem 1.2rem', borderRadius: '6px', border: 'none', background: T.GOLD, color: T.WHITE, fontWeight: 700, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}
          >
            <Send size={16} /> Save &amp; Publish
          </button>
        </div>
      </div>

      {/* Title block */}
      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{ height: '6px', backgroundColor: T.GOLD }} />
        <div style={{ height: '2px', backgroundColor: T.DEEP_TEAL, marginBottom: '1rem' }} />
        <label style={{ color: T.MIST, fontSize: '0.65rem', fontFamily: SERIF, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Title line 1</label>
        <TextInput value={data.title_line1} onChange={(e) => setField('title_line1', e.target.value)} testid="edit-title-line1" style={{ fontFamily: SERIF, fontSize: '1.8rem', fontWeight: 700, color: T.DEEP_TEAL, marginBottom: '0.5rem' }} />
        <label style={{ color: T.MIST, fontSize: '0.65rem', fontFamily: SERIF, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Title line 2</label>
        <TextInput value={data.title_line2} onChange={(e) => setField('title_line2', e.target.value)} testid="edit-title-line2" style={{ fontFamily: SERIF, fontSize: '1.4rem', color: T.GOLD, marginBottom: '0.5rem' }} />
        <label style={{ color: T.MIST, fontSize: '0.65rem', fontFamily: SERIF, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Subtitle</label>
        <AutoTextarea value={data.subtitle} onChange={(e) => setField('subtitle', e.target.value)} testid="edit-subtitle" style={{ fontFamily: SERIF, fontStyle: 'italic', color: T.SLATE, marginBottom: '0.5rem' }} />
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: '200px' }}>
            <label style={{ color: T.MIST, fontSize: '0.65rem', fontFamily: SERIF, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Authors</label>
            <TextInput value={data.author} onChange={(e) => setField('author', e.target.value)} testid="edit-author" style={{ fontFamily: SERIF, color: T.SLATE }} />
          </div>
          <div style={{ flex: 1, minWidth: '160px' }}>
            <label style={{ color: T.MIST, fontSize: '0.65rem', fontFamily: SERIF, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Date</label>
            <TextInput value={data.pub_date} onChange={(e) => setField('pub_date', e.target.value)} testid="edit-date" style={{ fontFamily: SERIF, color: T.SLATE }} />
          </div>
        </div>
      </div>

      {/* Keywords */}
      <div style={{ borderTop: `1px solid ${T.RULE_LINE}`, borderBottom: `1px solid ${T.RULE_LINE}`, padding: '0.75rem 0', margin: '1.25rem 0' }}>
        <div style={{ color: T.MIST, fontSize: '0.65rem', fontFamily: SERIF, textTransform: 'uppercase', letterSpacing: '0.15em', marginBottom: '0.4rem' }}>Keywords</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', alignItems: 'center' }}>
          {data.keywords.map((kw, i) => (
            <span key={i} data-testid={`keyword-chip-${i}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', background: T.GOLD_BG, color: T.SLATE, fontFamily: SERIF, fontStyle: 'italic', fontSize: '0.9rem', borderRadius: '999px', padding: '0.2rem 0.6rem', border: `1px solid ${T.RULE_LINE}` }}>
              {kw}
              <button type="button" onClick={() => removeKeyword(i)} data-testid={`keyword-remove-${i}`} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#dc2626', lineHeight: 1 }}><X size={12} /></button>
            </span>
          ))}
          <input
            type="text"
            value={newKeyword}
            data-testid="keyword-input"
            onChange={(e) => setNewKeyword(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addKeyword(); } }}
            placeholder="add keyword + Enter"
            style={{ border: `1px dashed ${T.RULE_LINE}`, borderRadius: '999px', padding: '0.2rem 0.6rem', fontFamily: SERIF, fontSize: '0.85rem', minWidth: '160px' }}
          />
        </div>
      </div>

      {/* Sections */}
      {data.sections.map((section, si) => (
        <section key={si} data-testid={`edit-section-${si}`} style={{ marginTop: '2rem' }}>
          {/* Section header */}
          <div style={{ display: 'flex', alignItems: 'stretch', marginBottom: '1rem' }}>
            <div style={{ backgroundColor: T.GOLD, color: T.WHITE, fontFamily: SERIF, fontSize: '1.4rem', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0.4rem 0.9rem', minWidth: '3.2rem' }}>
              {String(si + 1).padStart(2, '0')}
            </div>
            <div style={{ backgroundColor: T.DEEP_TEAL, flex: 1, display: 'flex', alignItems: 'center', padding: '0.3rem 0.8rem', gap: '0.5rem' }}>
              <input
                type="text"
                value={section.title}
                data-testid={`edit-section-title-${si}`}
                onChange={(e) => setSectionTitle(si, e.target.value)}
                style={{ flex: 1, background: 'transparent', border: '1px dashed rgba(255,255,255,0.4)', borderRadius: '4px', color: T.WHITE, fontFamily: SERIF, fontSize: '1.05rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em', padding: '0.25rem 0.5rem' }}
              />
              <CtrlButton onClick={() => moveSection(si, -1)} title="Move section up" testid={`section-${si}-up`}><ArrowUp size={14} /></CtrlButton>
              <CtrlButton onClick={() => moveSection(si, 1)} title="Move section down" testid={`section-${si}-down`}><ArrowDown size={14} /></CtrlButton>
              <CtrlButton onClick={() => deleteSection(si)} title="Delete section" testid={`section-${si}-delete`} danger><Trash2 size={14} /></CtrlButton>
            </div>
          </div>

          {/* Blocks */}
          {section.blocks.map((block, bi) => renderBlockEditor(section, si, block, bi))}

          <AddBlockBar onAdd={(type) => addBlock(si, type)} testidPrefix={`add-block-${si}`} />
        </section>
      ))}

      {/* Add section */}
      <button
        type="button"
        onClick={addSection}
        data-testid="add-section-button"
        style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', marginTop: '1.5rem', padding: '0.5rem 1rem', borderRadius: '6px', border: `1px dashed ${T.DEEP_TEAL}`, background: T.WHITE, color: T.DEEP_TEAL, fontFamily: SERIF, fontWeight: 700, cursor: 'pointer' }}
      >
        <Plus size={16} /> Add Section
      </button>

      {showPublish && (
        <PublishModal
          publication={{ ...publication, title: (data.title_line1 + (data.title_line2 ? ' ' + data.title_line2 : '')).trim() }}
          categories={categories}
          onClose={() => setShowPublish(false)}
          onSubmit={handlePublishSubmit}
          submitLabel="Publish Now"
        />
      )}
    </div>
  );
}
